"use client";

import { useNoteLabel, useNoteNotation } from "@/hooks/use-notation";
import {
  relativeTonality,
  tonalityLabel,
  type Tonality,
} from "@/lib/music/tonality";
import { CheckIcon, LoaderIcon } from "../atoms/icons";
import {
  FONT_MONO,
  type ApplyState,
} from "./tonality-picker-helpers";

type Props = {
  tonality: Tonality | null;
  appliedSemis: number;
  sourceTonality: Tonality | null;
  applyState: ApplyState;
  open: boolean;
  onClick: () => void;
};

export function TonalityPickerChip({
  tonality,
  appliedSemis,
  sourceTonality,
  applyState,
  open,
  onClick,
}: Props) {
  const toNoteLabel = useNoteLabel();
  const noteNotation = useNoteNotation();

  const rel = tonality ? relativeTonality(tonality) : null;
  const hasPiece = sourceTonality != null;
  const transposed = hasPiece && appliedSemis !== 0;
  const sign = appliedSemis > 0 ? "+" : "";

  let chipBackground: string = "rgba(255, 255, 255, 0.06)";
  if (open) {
    chipBackground = "var(--color-accent-subtle)";
  }

  const chipColor = tonality
    ? "var(--color-text-primary)"
    : "var(--color-text-muted)";

  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={open}
      aria-haspopup="dialog"
      /* Cible tactile étendue en hauteur, comme les autres contrôles de 28 px
         de la toolbar : le chip est large, c'est la verticale qui manque.

         `whitespace-nowrap` : le libellé porte des espaces (« Do majeur ·
         rel. La mineur »), donc des points de coupure. Comprimé par la
         toolbar, il repassait à la ligne dans un bouton en `h-7` et débordait
         par le bas. Un contrôle de hauteur fixe ne se replie pas, il abrège —
         voir la relative ci-dessous. */
      className="relative inline-flex h-7 items-center gap-2 whitespace-nowrap rounded-md px-2.5 text-[12px] font-medium transition-colors cursor-pointer pointer-coarse:after:absolute pointer-coarse:after:content-[''] pointer-coarse:after:-inset-y-2 pointer-coarse:after:inset-x-0"
      style={{
        background: chipBackground,
        color: chipColor,
        border: `1px solid ${
          open ? "var(--color-glass-border)" : "transparent"
        }`,
        fontFamily: tonality ? FONT_MONO : undefined,
      }}
    >
      {hasPiece && sourceTonality && (
        <>
          <span
            className="text-[9px] uppercase tracking-[0.1em]"
            style={{ color: "var(--color-text-muted)", fontFamily: "inherit" }}
          >
            ♪
          </span>
          <span
            className="text-[11px]"
            style={{ color: "var(--color-text-secondary)" }}
          >
            {toNoteLabel(sourceTonality.tonic)}
          </span>
          <span
            className="text-[10px]"
            style={{ color: "var(--color-text-muted)" }}
          >
            →
          </span>
        </>
      )}

      {tonality ? (
        <>
          <span style={{ fontWeight: 500 }}>
            {tonalityLabel(tonality, noteNotation)}
          </span>
          {/* La relative est un rappel, pas une commande : c'est la première
              chose qui saute en viewport court, où le chip pèse près d'un
              tiers de la toolbar. La tonique, elle, reste toujours lisible.
              Replié en CSS et non via `useShortViewport`, sinon le desktop
              verrait la forme abrégée le temps d'une frame. */}
          {rel && (
            <span
              className="text-[11px] [@media(max-height:480px)]:hidden"
              style={{ color: "var(--color-text-secondary)" }}
            >
              · rel. {tonalityLabel(rel, noteNotation)}
            </span>
          )}
          {transposed && (
            <span
              className="rounded px-1 py-px text-[10px] font-semibold tabular-nums"
              style={{
                background: "rgba(139, 92, 246, 0.25)",
                color: "var(--color-accent-bright)",
                fontFamily: "inherit",
              }}
            >
              {sign}
              {appliedSemis}
            </span>
          )}
        </>
      ) : (
        <>
          <span
            className="inline-flex h-3.5 w-3.5 items-center justify-center rounded text-[10px]"
            style={{
              border: "1px dashed var(--color-text-muted)",
              color: "var(--color-text-muted)",
            }}
          >
            ♭
          </span>
          <span className="italic" style={{ fontWeight: 500 }}>
            Choisir une tonalité
          </span>
        </>
      )}

      {applyState === "loading" && (
        <LoaderIcon
          size={12}
          className="animate-spin"
          style={{ color: "var(--color-accent)" }}
        />
      )}
      {applyState === "done" && (
        <span
          className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-full"
          style={{
            background: "rgba(52, 211, 153, 0.18)",
            color: "var(--color-ok)",
          }}
        >
          <CheckIcon size={9} strokeWidth={3} />
        </span>
      )}

      <svg
        width="9"
        height="9"
        viewBox="0 0 12 12"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      >
        <path d="M3 4.5l3 3 3-3" />
      </svg>
    </button>
  );
}
